import React, { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import CollapsibleSection from './CollapsibleSection';
import '../../styles/report-markdown.css';

marked.setOptions({ gfm: true, breaks: false, headerIds: false, mangle: false });

const C = {
  orange: "#F47920",
  blue: "#3B82F6",
  blueLight: "#EFF6FF",
  green: "#22C55E",
  greenLight: "#DCFCE7",
  greenBorder: "#BBF7D0",
  purple: "#8B5CF6",
  purpleLight: "#F3F0FF",
  purpleBorder: "#DDD6FE",
  amber: "#F59E0B",
  rose: "#F43F5E",
  g50: "#F9FAFB", g100: "#F3F4F6", g400: "#9CA3AF", g500: "#6B7280", g700: "#374151",
};

function RatingCircle({ rating, maxRating = 10 }) {
  const pct = (rating / maxRating) * 100;
  const r = 32;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (pct / 100) * circumference;

  const getColor = (val) => {
    if (val >= 8) return C.green;
    if (val >= 6) return C.blue;
    if (val >= 4) return C.amber;
    return C.rose;
  };
  const color = getColor(rating);

  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <svg width="80" height="80" viewBox="0 0 80 80" className="transform -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#F3F4F6" strokeWidth="6" />
        <circle
          cx="40" cy="40" r={r} fill="none"
          stroke={color} strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-black leading-none" style={{ color }}>{rating}</span>
        <span className="text-[9px] font-bold text-gray-400">/ {maxRating}</span>
      </div>
    </div>
  );
}

function VerdictBadge({ verdict }) {
  const config = {
    "Ready": { bg: C.greenLight, border: C.greenBorder, color: C.green, icon: "🏆" },
    "Almost ready": { bg: C.blueLight, border: "#BFDBFE", color: C.blue, icon: "💪" },
    "Needs work": { bg: "#FFF7ED", border: "#FED7AA", color: C.amber, icon: "📝" },
    "Not ready": { bg: "#FFF1F2", border: "#FECDD3", color: C.rose, icon: "🔄" },
  };
  const c = config[verdict] || config["Almost ready"];

  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border shadow-sm transition-all"
         style={{ background: c.bg, borderColor: c.border }}>
      <span className="text-sm">{c.icon}</span>
      <span className="text-[13px] font-bold" style={{ color: c.color }}>{verdict}</span>
    </div>
  );
}

function normalizeReportList(items, kind) {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      if (!item) return null;
      if (typeof item === 'string') {
        const text = item.trim();
        return text ? { title: '', body: text, accent: '' } : null;
      }

      if (typeof item !== 'object') {
        const text = String(item).trim();
        return text ? { title: '', body: text, accent: '' } : null;
      }

      const area = String(item.area || '').trim();

      if (kind === 'strength') {
        const observation = String(item.observation || '').trim();
        const whyStrong = String(item.why_strong || '').trim();
        const signal = String(item.signal || '').trim();
        const body = [observation, whyStrong].filter(Boolean).join(' ');
        return area || body || signal
          ? { title: area, body, accent: signal }
          : null;
      }

      const gap = String(item.gap || '').trim();
      const howToImprove = String(item.how_to_improve || '').trim();
      const body = [gap, howToImprove].filter(Boolean).join(' ');
      return area || body
        ? { title: area, body, accent: '' }
        : null;
    })
    .filter(Boolean);
}

export default function FinalReport({ report }) {
  if (!report) return null;

  const strengths = normalizeReportList(report.strengths, 'strength');
  const improvements = normalizeReportList(report.improvements, 'improvement');
  const reportMarkdownHtml = useMemo(() => {
    const md = report.report_markdown || report.reportMarkdown;
    if (typeof md !== 'string' || !md.trim()) return '';
    return DOMPurify.sanitize(marked.parse(md));
  }, [report.report_markdown, report.reportMarkdown]);

  return (
    <CollapsibleSection
      icon="📋"
      title="Final Report"
      subtitle="AI-generated interview assessment"
      defaultOpen={true}
    >
      <div className="px-5 pb-6">
        {/* Rating + Verdict Header */}
        <div className="flex items-center gap-5 py-5 border-b border-gray-100 mb-5">
          <RatingCircle rating={report.rating} />
          <div className="flex-1 min-w-0">
            <VerdictBadge verdict={report.verdict} />
            <div className="text-[13px] text-gray-500 leading-relaxed mt-2 line-clamp-2">
              {report.rating_comment || report.ratingComment}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-sm">📝</span>
            <span className="text-[11px] font-extrabold uppercase tracking-widest text-gray-400">Summary</span>
          </div>
          <div className="text-[14px] text-slate-700 leading-relaxed p-4 rounded-xl bg-gray-50 border-l-4 border-blue-500">
            {report.summary}
          </div>
        </div>

        {/* Strengths */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm">💪</span>
            <span className="text-[11px] font-extrabold uppercase tracking-widest text-green-600">Strengths</span>
          </div>
          <div className="flex flex-col gap-2">
            {strengths.map((item, i) => (
              <div key={i} className="flex gap-3 p-3 rounded-xl bg-green-50/50 border border-green-100 transition-hover hover:bg-green-50">
                <span className="w-5 h-5 rounded-md bg-green-100 flex items-center justify-center text-[10px] font-black text-green-600 flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  {item.title && (
                    <div className="text-[12px] font-extrabold uppercase tracking-wide text-green-700">
                      {item.title}
                    </div>
                  )}
                  {item.body && (
                    <div className="text-[13px] text-slate-700 leading-snug">
                      {item.body}
                    </div>
                  )}
                  {item.accent && (
                    <div className="mt-1 text-[12px] font-medium text-green-800/80">
                      Signal: {item.accent}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Areas for Improvement */}
        {improvements.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm">🌱</span>
              <span className="text-[11px] font-extrabold uppercase tracking-widest text-purple-600">Areas for Improvement</span>
            </div>
            <div className="flex flex-col gap-2">
              {improvements.map((item, i) => (
                <div key={i} className="flex gap-3 p-3 rounded-xl bg-purple-50/50 border border-purple-100 transition-hover hover:bg-purple-50">
                  <span className="w-5 h-5 rounded-md bg-purple-100 flex items-center justify-center text-[10px] font-black text-purple-600 flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    {item.title && (
                      <div className="text-[12px] font-extrabold uppercase tracking-wide text-purple-700">
                        {item.title}
                      </div>
                    )}
                    {item.body && (
                      <div className="text-[13px] text-slate-700 leading-snug">
                        {item.body}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next Steps */}
        {(report.next_steps || report.nextSteps) && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="text-sm">🗺️</span>
              <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#F47920]">Action Plan</span>
            </div>
            <div className="text-[14px] text-slate-700 leading-relaxed p-4 rounded-xl bg-orange-50/50 border-l-4 border-[#F47920]">
              {report.next_steps || report.nextSteps}
            </div>
          </div>
        )}

        {/* Full Coaching Report — expanded markdown narrative */}
        {reportMarkdownHtml && (
          <div className="mt-6 pt-5 border-t border-gray-100">
            <details className="group">
              <summary className="flex cursor-pointer items-center gap-2 text-[13px] font-bold text-slate-700 hover:text-slate-900 list-none [&::-webkit-details-marker]:hidden">
                <span className="text-sm">📖</span>
                <span className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500">Full Coaching Report</span>
                <span className="ml-auto text-[11px] font-medium text-slate-400 group-open:hidden">Read full report →</span>
                <span className="ml-auto text-[11px] font-medium text-slate-400 hidden group-open:inline">Collapse ↑</span>
              </summary>
              <div
                className="report-markdown mt-4 text-[14px] leading-relaxed text-slate-700"
                dangerouslySetInnerHTML={{ __html: reportMarkdownHtml }}
              />
            </details>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}
